<template>
  <div v-if="loading" class="form-detail-loading">
    <div class="form-detail-spinner"></div>
    <p>Loading form details...</p>
  </div>
  <div v-else-if="accessDenied" class="form-detail-loading">
    <p style="color: #d32f2f; font-weight: bold;">You are not allowed to access this form.</p>
    <router-link to="/forms" class="btn btn-link" style="margin-top: 1.5em;">Back to Forms</router-link>
  </div>
  <div v-else-if="form" class="form-detail-root">
    <div class="form-detail-card">
      <h2 class="form-detail-title">{{ form.title }}</h2>
      <form @submit.prevent="submitAnswers" class="form-detail-form">
        <div
          v-for="(question, idx) in form.questions"
          :key="question.id"
          class="form-detail-question"
        >
          <div class="form-detail-question-header">
            <span class="form-detail-question-number">{{ idx + 1 }}.</span>
            <span class="form-detail-question-text">{{ question.text }}</span>
          </div>
          <div
            v-if="question.type === 'range_feedback'"
            class="form-detail-options"
          >
            <label
              v-for="option in question.options"
              :key="option.id"
              class="form-detail-option"
              :class="{ selected: answers[question.id] == option.id }"
            >
              <input
                type="radio"
                :name="'question_' + question.id"
                :value="option.id"
                v-model="answers[question.id]"
                required
              />
              <span>{{ option.text }}</span>
            </label>
          </div>
          <div v-else-if="question.type === 'qna'" class="form-detail-qna">
            <input
              type="text"
              class="form-detail-qna-input"
              v-model="answers[question.id]"
              :placeholder="'Type your answer...'"
              required
            />
          </div>
        </div>
        <div class="form-detail-actions">
          <button type="submit" class="btn btn-primary">Submit</button>
          <router-link to="/forms" class="btn btn-link">Back to Forms</router-link>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { fetchFormDetail, submitFormAnswers, checkUserFormAccess } from "../../services/formService";
import { mapState } from "vuex";

export default {
  props: ["formId"],
  data() {
    return {
      form: null,
      answers: {},
      loading: true,
      accessDenied: false,
    };
  },
  computed: {
    ...mapState(["profileInfo"]),
  },
  async created() {
    const id = this.formId || this.$route.params.formId || "11";
    if (!this.profileInfo?.userId) {
      await this.$store.dispatch("fetchProfileInfo");
    }
    try {
      const res = await checkUserFormAccess(this.profileInfo.userId, id);
      if (!(res && res.message === "User can access this form")) {
        this.accessDenied = true;
        this.loading = false;
        return;
      }
    } catch (e) {
      this.accessDenied = true;
      this.loading = false;
      return;
    }
    this.form = await fetchFormDetail(id);
    if (this.form && this.form.questions) {
      this.form.questions.forEach((q) => {
        this.answers[q.id] = "";
      });
    }
    this.loading = false;
  },
  methods: {
    async submitAnswers() {
      if (!this.profileInfo?.userId) {
        await this.$store.dispatch("fetchProfileInfo");
      }
      const roll_number = this.profileInfo?.organization?.rollNumber || "";
      const responses = this.form.questions.map(q => {
        if (q.type === "range_feedback") {
          return {
            questionId: Number(q.id),
            roll_number,
            optionId: Number(this.answers[q.id])
          };
        } else if (q.type === "qna") {
          return {
            questionId: Number(q.id),
            roll_number,
            answer: this.answers[q.id]
          };
        }
        return null;
      }).filter(Boolean);

      const payload = {
        responses,
        isStudent: true
      };

      try {
        await submitFormAnswers(this.form.id, payload, this.profileInfo?.userId);
        this.form.questions.forEach(q => {
          this.answers[q.id] = "";
        });
        this.$notify
          ? this.$notify({ type: "success", text: "Answers submitted!" })
          : alert("Answers submitted!");
        this.$router.push({ name: "formsList" });
      } catch (e) {
        this.$notify
          ? this.$notify({ type: "error", text: "Submission failed!" })
          : alert("Submission failed!");
      }
    }
  }
};
</script>

<style scoped>
.form-detail-root {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  min-height: 70vh;
  background: var(--background, #f9fafb);
  padding: 32px 0;
}
.form-detail-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 16px rgba(60, 72, 88, 0.08);
  border: 1px solid #e5e7eb;
  max-width: 1100px;
  width: 96vw;
  min-width: 320px;
  padding: 32px 40px 24px 40px;
  margin: 0 2vw;
  transition: max-width 0.2s;
}
@media (max-width: 900px) {
  .form-detail-card {
    max-width: 98vw;
    padding: 24px 8px 16px 8px;
  }
}
.form-detail-title {
  font-size: 1.7rem;
  font-weight: 700;
  color: var(--primary, #1d3557);
  margin-bottom: 2rem;
  text-align: center;
  letter-spacing: 0.5px;
}
.form-detail-form {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}
.form-detail-question {
  margin-bottom: 0.5rem;
}
.form-detail-question-header {
  display: flex;
  align-items: flex-start;
  gap: 0.7em;
  margin-bottom: 0.7em;
}
.form-detail-question-number {
  color: var(--primary, #2563eb);
  font-weight: 600;
  font-size: 1.1em;
  margin-right: 0.3em;
}
.form-detail-question-text {
  color: #22223b;
  font-size: 1.08em;
  font-weight: 500;
}
.form-detail-options {
  display: flex;
  gap: 1.2em;
  flex-wrap: wrap;
}
.form-detail-option {
  display: flex;
  align-items: center;
  gap: 0.5em;
  padding: 0.35em 0.9em;
  border-radius: 6px;
  cursor: pointer;
  border: 1.5px solid transparent;
  font-size: 1em;
  background: #f6f8fa;
  font-weight: 500;
  transition: background 0.15s, border 0.15s;
}
.form-detail-option.selected,
.form-detail-option:hover {
  background: #e3eefd;
  border-color: var(--primary, #2563eb);
  color: var(--primary, #2563eb);
}
input[type="radio"] {
  accent-color: var(--primary, #2563eb);
  width: 1.1em;
  height: 1.1em;
}
.form-detail-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 2.5em;
  gap: 1em;
}
.btn {
  font-family: inherit;
  font-size: 1em;
  border-radius: 6px;
  padding: 0.6em 1.7em;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: background 0.15s, color 0.15s;
  outline: none;
  text-decoration: none;
  display: inline-block;
}
.btn-primary {
  background: var(--primary, #2563eb);
  color: #fff;
  border: none;
}
.btn-primary:hover {
  background: #1d4ed8;
}
.btn-link {
  background: transparent;
  color: var(--primary, #2563eb);
  text-decoration: underline;
  padding: 0.6em 0.8em;
  border: none;
}
.btn-link:hover {
  color: #1d4ed8;
}
.form-detail-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 5em;
}
.form-detail-spinner {
  border: 4px solid #e5e7eb;
  border-top: 4px solid var(--primary, #2563eb);
  border-radius: 50%;
  width: 36px;
  height: 36px;
  animation: spin 1s linear infinite;
  margin-bottom: 1em;
}
@keyframes spin {
  0% { transform: rotate(0deg);}
  100% { transform: rotate(360deg);}
}
.form-detail-qna {
  margin-top: 0.5em;
}
.form-detail-qna-input {
  width: 100%;
  padding: 0.6em 1em;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 1em;
  background: #f6f8fa;
  transition: border 0.15s;
}
.form-detail-qna-input:focus {
  border-color: var(--primary, #2563eb);
  outline: none;
  background: #fff;
}
</style>